import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { X, Search, Check, Plus, Minus, Image, ChevronDown, ChevronUp } from 'lucide-react';

// Hard-coded movement categories (won't change)
const MOVEMENT_CATEGORIES = [
  { id: 'upper-body-push', name: 'Upper Body Push', color: '#e53935' },
  { id: 'upper-body-pull', name: 'Upper Body Pull', color: '#8e24aa' },
  { id: 'lower-body', name: 'Lower Body', color: '#1e88e5' },
  { id: 'core', name: 'Core', color: '#00acc1' },
  { id: 'cardio', name: 'Cardio', color: '#ff5722' },
  { id: 'upper-body-warmup', name: 'Upper Body Warm-up', color: '#ff9800' },
  { id: 'lower-body-warmup', name: 'Lower Body Warm-up', color: '#4caf50' },
  { id: 'total-body-warmup', name: 'Total Body Warm-up', color: '#9c27b0' },
  { id: 'movement-warmup', name: 'Movement Warm-up', color: '#607d8b' },
];

function EditTagsModal({ asset, onClose, onSave }) {
  const [activeTab, setActiveTab] = useState('equipment');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Equipment state - stores { equipmentId: quantity }
  const [selectedEquipment, setSelectedEquipment] = useState(() => {
    if (asset.equipmentById) {
      return asset.equipmentById;
    }
    return {};
  });
  
  // Movement state
  const [selectedMovements, setSelectedMovements] = useState(asset.movementCategories || []);
  
  // Body groups state
  const [primaryBodyGroups, setPrimaryBodyGroups] = useState(asset.primaryBodyGroups || []);
  const [secondaryBodyGroups, setSecondaryBodyGroups] = useState(asset.secondaryBodyGroups || []);
  
  // Programming notes
  const [programmingNotes, setProgrammingNotes] = useState(asset.programmingNotes || '');
  
  // Substitutes state
  const [selectedSubstitutes, setSelectedSubstitutes] = useState(asset.substitutes || []);
  const [allAssets, setAllAssets] = useState([]);
  
  // Legacy support
  useEffect(() => {
    if (asset.bodyGroups && asset.bodyGroups.length > 0 && !asset.primaryBodyGroups) {
      setPrimaryBodyGroups(asset.bodyGroups);
    }
  }, [asset]);
  
  const [equipmentList, setEquipmentList] = useState([]);
  const [bodyGroupList, setBodyGroupList] = useState([]);
  const [hoveredEquipment, setHoveredEquipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});

  // Add body group modal state
  const [showAddBodyGroup, setShowAddBodyGroup] = useState(false);
  const [newBodyGroupName, setNewBodyGroupName] = useState('');
  const [newBodyGroupCategory, setNewBodyGroupCategory] = useState('Upper Body');
  const [newBodyGroupColor, setNewBodyGroupColor] = useState('#2196F3');

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch equipment from master list (read-only)
        const equipmentSnapshot = await getDocs(collection(db, 'equipment'));
        const equipment = equipmentSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        equipment.sort((a, b) => a.name.localeCompare(b.name));
        setEquipmentList(equipment);

        const bodyGroupsSnapshot = await getDocs(collection(db, 'bodyGroups'));
        let bodyGroups = bodyGroupsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        if (bodyGroups.length === 0) {
          bodyGroups = await seedDefaultBodyGroups();
        }
        
        bodyGroups.sort((a, b) => a.name.localeCompare(b.name));
        setBodyGroupList(bodyGroups);

        // Fetch all video assets for substitutes
        const assetsSnapshot = await getDocs(collection(db, 'assets'));
        const assets = assetsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(a => a.type === 'video' && a.id !== asset.id)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setAllAssets(assets);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [asset.id]);

  const seedDefaultBodyGroups = async () => {
    const defaultBodyGroups = [
      { name: 'Chest', category: 'Upper Body', color: '#e53935' },
      { name: 'Back', category: 'Upper Body', color: '#8e24aa' },
      { name: 'Mid-Back', category: 'Upper Body', color: '#7b1fa2' },
      { name: 'Lats', category: 'Upper Body', color: '#512da8' },
      { name: 'Rear Delts', category: 'Upper Body', color: '#303f9f' },
      { name: 'Shoulders', category: 'Upper Body', color: '#1976d2' },
      { name: 'Biceps', category: 'Upper Body', color: '#0288d1' },
      { name: 'Triceps', category: 'Upper Body', color: '#0097a7' },
      { name: 'Forearms', category: 'Upper Body', color: '#00796b' },
      { name: 'Obliques', category: 'Core', color: '#388e3c' },
      { name: 'Rectus Abdominis', category: 'Core', color: '#689f38' },
      { name: 'Transverse Abdominis', category: 'Core', color: '#afb42b' },
      { name: 'Hip Flexors', category: 'Core', color: '#fbc02d' },
      { name: 'Glutes', category: 'Lower Body', color: '#f57c00' },
      { name: 'Quads', category: 'Lower Body', color: '#e64a19' },
      { name: 'Hamstrings', category: 'Lower Body', color: '#5d4037' },
      { name: 'Calves', category: 'Lower Body', color: '#455a64' },
      { name: 'Adductors', category: 'Lower Body', color: '#d81b60' },
      { name: 'Abductors', category: 'Lower Body', color: '#c2185b' },
    ];

    const seededGroups = [];
    for (const group of defaultBodyGroups) {
      const docRef = await addDoc(collection(db, 'bodyGroups'), { ...group, createdAt: Date.now() });
      seededGroups.push({ id: docRef.id, ...group });
    }
    return seededGroups;
  };

  const filterItems = (items) => {
    if (!searchQuery.trim()) return items;
    return items.filter(item =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.abbrev && item.abbrev.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  const groupedBodyGroups = bodyGroupList.reduce((acc, group) => {
    const category = group.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(group);
    return acc;
  }, {});

  const toggleEquipment = (equipmentId) => {
    setSelectedEquipment(prev => {
      if (prev[equipmentId] !== undefined) {
        const { [equipmentId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [equipmentId]: 1 };
    });
  };

  const updateQuantity = (equipmentId, delta) => {
    setSelectedEquipment(prev => {
      const current = prev[equipmentId] || 1;
      const newQty = Math.max(1, current + delta);
      return { ...prev, [equipmentId]: newQty };
    });
  };

  const toggleMovement = (movementId) => {
    setSelectedMovements(prev =>
      prev.includes(movementId) ? prev.filter(id => id !== movementId) : [...prev, movementId]
    );
  };

  const togglePrimaryBodyGroup = (name) => {
    if (!primaryBodyGroups.includes(name)) {
      setSecondaryBodyGroups(prev => prev.filter(n => n !== name));
    }
    setPrimaryBodyGroups(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const toggleSecondaryBodyGroup = (name) => {
    if (!secondaryBodyGroups.includes(name)) {
      setPrimaryBodyGroups(prev => prev.filter(n => n !== name));
    }
    setSecondaryBodyGroups(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleSubstitute = (assetId) => {
    setSelectedSubstitutes(prev =>
      prev.includes(assetId) ? prev.filter(id => id !== assetId) : [...prev, assetId]
    );
  };

  const filterAssets = (assets) => {
    if (!searchQuery.trim()) return assets;
    return assets.filter(a =>
      (a.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (a.movementCategories || []).some(m => m.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (a.primaryBodyGroups || []).some(b => b.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build equipment names array for backward compatibility
      const equipmentNames = Object.keys(selectedEquipment).map(id => {
        const eq = equipmentList.find(e => e.id === id);
        return eq?.name || id;
      });
      
      const updateData = {
        equipmentById: selectedEquipment,
        equipmentTags: equipmentNames,
        equipmentQuantities: Object.fromEntries(
          Object.entries(selectedEquipment).map(([id, qty]) => {
            const eq = equipmentList.find(e => e.id === id);
            return [eq?.name || id, qty];
          })
        ),
        movementCategories: selectedMovements,
        primaryBodyGroups: primaryBodyGroups,
        secondaryBodyGroups: secondaryBodyGroups,
        bodyGroups: [...primaryBodyGroups, ...secondaryBodyGroups],
        programmingNotes: programmingNotes.trim(),
        substitutes: selectedSubstitutes
      };
      await updateDoc(doc(db, 'assets', asset.id), updateData);
      onSave(updateData);
      onClose();
    } catch (error) {
      console.error('Error saving tags:', error);
      alert('Error saving tags: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddBodyGroup = async () => {
    if (!newBodyGroupName.trim()) return;
    try {
      const newGroup = { name: newBodyGroupName.trim(), category: newBodyGroupCategory, color: newBodyGroupColor, createdAt: Date.now() };
      const docRef = await addDoc(collection(db, 'bodyGroups'), newGroup);
      setBodyGroupList(prev => [...prev, { id: docRef.id, ...newGroup }].sort((a, b) => a.name.localeCompare(b.name)));
      setShowAddBodyGroup(false);
      setNewBodyGroupName('');
      setNewBodyGroupCategory('Upper Body');
      setNewBodyGroupColor('#2196F3');
      setSearchQuery('');
    } catch (error) {
      console.error('Error adding body group:', error);
      alert('Error adding body group: ' + error.message);
    }
  };

  const showAddNewBodyGroupOption = activeTab === 'bodygroups' && searchQuery.trim() && !bodyGroupList.some(bg => bg.name.toLowerCase() === searchQuery.trim().toLowerCase());

  const tabColors = { equipment: '#2196F3', movement: '#9c27b0', bodygroups: '#4CAF50', notes: '#ff9800', substitutes: '#e91e63' };
  const selectedEquipmentCount = Object.keys(selectedEquipment).length;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        {/* Main Modal */}
        <div style={{ width: '480px', background: '#fff', borderRadius: '12px', boxShadow: '0 8px 40px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #eee' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#333' }}>Edit Tags</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}><X size={20} color="#666" /></button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
            {[
              { id: 'equipment', label: 'Equip', count: selectedEquipmentCount },
              { id: 'movement', label: 'Move', count: selectedMovements.length },
              { id: 'bodygroups', label: 'Body', count: primaryBodyGroups.length + secondaryBodyGroups.length },
              { id: 'notes', label: 'Notes', count: programmingNotes.trim() ? 1 : 0 },
              { id: 'substitutes', label: 'Subs', count: selectedSubstitutes.length }
            ].map(tab => (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSearchQuery(''); setHoveredEquipment(null); }}
                style={{ flex: 1, padding: '12px 8px', background: 'none', border: 'none', borderBottom: activeTab === tab.id ? `2px solid ${tabColors[tab.id]}` : '2px solid transparent', cursor: 'pointer', fontSize: '12px', fontWeight: 500, color: activeTab === tab.id ? tabColors[tab.id] : '#888', transition: 'all 0.15s' }}>
                {tab.label} {tab.count > 0 && <span style={{ opacity: 0.7 }}>({tab.count})</span>}
              </button>
            ))}
          </div>

          {/* EQUIPMENT TAB */}
          {activeTab === 'equipment' && (
            <>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', minHeight: '70px' }}>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Required Equipment</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {Object.entries(selectedEquipment).map(([equipmentId, qty]) => {
                    const equipment = equipmentList.find(e => e.id === equipmentId);
                    if (!equipment) return null;
                    return (
                      <span key={equipmentId} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#e3f2fd', color: '#1976d2', padding: '6px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: 500 }}>
                        {equipment.name} <span style={{ opacity: 0.7 }}>×{qty}</span>
                        <button onClick={() => toggleEquipment(equipmentId)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', opacity: 0.7 }}><X size={14} color="#1976d2" /></button>
                      </span>
                    );
                  })}
                  {selectedEquipmentCount === 0 && <span style={{ color: '#bbb', fontSize: '13px', fontStyle: 'italic' }}>No equipment tagged</span>}
                </div>
              </div>

              <div style={{ padding: '12px 20px' }}>
                <div style={{ position: 'relative' }}>
                  <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                  <input type="text" placeholder="Search equipment..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px 10px 40px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ maxHeight: '280px', overflowY: 'auto', padding: '0 12px 12px' }}>
                {loading ? <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Loading equipment...</div> : (
                  <>
                    {filterItems(equipmentList).length === 0 && (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                        {equipmentList.length === 0 
                          ? 'No equipment found. Add equipment on the Equipment Profile page.'
                          : 'No equipment matches your search.'
                        }
                      </div>
                    )}
                    {filterItems(equipmentList).map(equipment => {
                      const isSelected = selectedEquipment[equipment.id] !== undefined;
                      const quantity = selectedEquipment[equipment.id] || 1;
                      return (
                        <div key={equipment.id} onMouseEnter={() => setHoveredEquipment(equipment)} onMouseLeave={() => setHoveredEquipment(null)}
                          style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '8px', background: isSelected ? '#e3f2fd' : hoveredEquipment?.id === equipment.id ? '#f5f5f5' : 'transparent', transition: 'background 0.15s' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '6px', backgroundImage: equipment.imageUrl ? `url(${equipment.imageUrl})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: equipment.imageUrl ? 'transparent' : '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid #e0e0e0' }}>
                            {!equipment.imageUrl && <Image size={16} color="#bbb" />}
                          </div>
                          <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => toggleEquipment(equipment.id)}>
                            <div style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>{equipment.name}</div>
                            {equipment.abbrev && <div style={{ fontSize: '12px', color: '#888' }}>{equipment.abbrev}</div>}
                          </div>
                          {isSelected && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'white', border: '1px solid #ddd', borderRadius: '6px', padding: '2px' }}>
                              <button onClick={(e) => { e.stopPropagation(); updateQuantity(equipment.id, -1); }} style={{ width: '28px', height: '28px', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', borderRadius: '4px' }}><Minus size={14} /></button>
                              <span style={{ minWidth: '24px', textAlign: 'center', fontSize: '14px', fontWeight: 600, color: '#333' }}>{quantity}</span>
                              <button onClick={(e) => { e.stopPropagation(); updateQuantity(equipment.id, 1); }} style={{ width: '28px', height: '28px', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', borderRadius: '4px' }}><Plus size={14} /></button>
                            </div>
                          )}
                          <div onClick={() => toggleEquipment(equipment.id)} style={{ width: '22px', height: '22px', borderRadius: '50%', border: isSelected ? 'none' : '2px solid #ddd', background: isSelected ? '#2196F3' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                            {isSelected && <Check size={13} color="white" strokeWidth={3} />}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </>
          )}

          {/* MOVEMENT TAB */}
          {activeTab === 'movement' && (
            <>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', minHeight: '70px' }}>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Movement Categories</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {selectedMovements.map(movementId => {
                    const movement = MOVEMENT_CATEGORIES.find(m => m.id === movementId);
                    if (!movement) return null;
                    return (
                      <span key={movementId} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: movement.color + '20', color: movement.color, padding: '6px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: 500 }}>
                        {movement.name}
                        <button onClick={() => toggleMovement(movementId)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', opacity: 0.7 }}><X size={14} color={movement.color} /></button>
                      </span>
                    );
                  })}
                  {selectedMovements.length === 0 && <span style={{ color: '#bbb', fontSize: '13px', fontStyle: 'italic' }}>No movement category selected</span>}
                </div>
              </div>
              <div style={{ maxHeight: '340px', overflowY: 'auto', padding: '12px' }}>
                {MOVEMENT_CATEGORIES.map(movement => {
                  const isSelected = selectedMovements.includes(movement.id);
                  return (
                    <div key={movement.id} onClick={() => toggleMovement(movement.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '8px', cursor: 'pointer', background: isSelected ? movement.color + '15' : 'transparent', transition: 'background 0.15s' }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: movement.color, flexShrink: 0 }} />
                      <div style={{ flex: 1, fontSize: '14px', fontWeight: 500, color: '#333' }}>{movement.name}</div>
                      <div style={{ width: '22px', height: '22px', borderRadius: '50%', border: isSelected ? 'none' : '2px solid #ddd', background: isSelected ? movement.color : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isSelected && <Check size={13} color="white" strokeWidth={3} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* BODY GROUPS TAB */}
          {activeTab === 'bodygroups' && (
            <>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', minHeight: '90px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Primary Muscle Groups</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {primaryBodyGroups.map(name => {
                      const group = bodyGroupList.find(g => g.name === name);
                      const color = group?.color || '#666';
                      return (
                        <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: color + '20', color: color, padding: '6px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: 500 }}>
                          {name}
                          <button onClick={() => togglePrimaryBodyGroup(name)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', opacity: 0.7 }}><X size={14} color={color} /></button>
                        </span>
                      );
                    })}
                    {primaryBodyGroups.length === 0 && <span style={{ color: '#bbb', fontSize: '13px', fontStyle: 'italic' }}>None selected</span>}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Secondary Muscle Groups</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {secondaryBodyGroups.map(name => {
                      const group = bodyGroupList.find(g => g.name === name);
                      const color = group?.color || '#666';
                      return (
                        <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: color + '15', color: color, padding: '5px 9px', borderRadius: '6px', fontSize: '12px', fontWeight: 500, border: `1px dashed ${color}50` }}>
                          {name}
                          <button onClick={() => toggleSecondaryBodyGroup(name)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', opacity: 0.7 }}><X size={12} color={color} /></button>
                        </span>
                      );
                    })}
                    {secondaryBodyGroups.length === 0 && <span style={{ color: '#bbb', fontSize: '13px', fontStyle: 'italic' }}>None selected</span>}
                  </div>
                </div>
              </div>

              <div style={{ padding: '12px 20px' }}>
                <div style={{ position: 'relative' }}>
                  <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                  <input type="text" placeholder="Search or add body group..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px 10px 40px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ maxHeight: '250px', overflowY: 'auto', padding: '0 12px 12px' }}>
                {loading ? <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Loading body groups...</div> : (
                  <>
                    {Object.entries(groupedBodyGroups).map(([category, groups]) => {
                      const filteredGroups = filterItems(groups);
                      if (filteredGroups.length === 0) return null;
                      return (
                        <div key={category} style={{ marginBottom: '8px' }}>
                          <div onClick={() => toggleSection(category)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f5f5f5', borderRadius: '6px', cursor: 'pointer', marginBottom: '4px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{category}</span>
                            {collapsedSections[category] ? <ChevronDown size={16} color="#888" /> : <ChevronUp size={16} color="#888" />}
                          </div>
                          {!collapsedSections[category] && filteredGroups.map(group => {
                            const isPrimary = primaryBodyGroups.includes(group.name);
                            const isSecondary = secondaryBodyGroups.includes(group.name);
                            return (
                              <div key={group.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '8px', background: isPrimary ? group.color + '15' : isSecondary ? group.color + '08' : 'transparent', transition: 'background 0.15s' }}>
                                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: group.color, flexShrink: 0 }} />
                                <div style={{ flex: 1, fontSize: '14px', fontWeight: 500, color: '#333' }}>{group.name}</div>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <button onClick={() => togglePrimaryBodyGroup(group.name)}
                                    style={{ padding: '4px 10px', borderRadius: '4px', border: isPrimary ? 'none' : '1px solid #ddd', background: isPrimary ? group.color : 'white', color: isPrimary ? 'white' : '#666', fontSize: '11px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}>1°</button>
                                  <button onClick={() => toggleSecondaryBodyGroup(group.name)}
                                    style={{ padding: '4px 10px', borderRadius: '4px', border: isSecondary ? 'none' : '1px solid #ddd', background: isSecondary ? group.color + 'aa' : 'white', color: isSecondary ? 'white' : '#666', fontSize: '11px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}>2°</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                    {showAddNewBodyGroupOption && (
                      <div onClick={() => { setNewBodyGroupName(searchQuery.trim()); setShowAddBodyGroup(true); }}
                        style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', background: '#e8f5e9', border: '1px dashed #4caf50', marginTop: '8px' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#c8e6c9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={16} color="#388e3c" /></div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: 500, color: '#2e7d32' }}>Add "{searchQuery.trim()}"</div>
                          <div style={{ fontSize: '12px', color: '#4caf50' }}>Create new body group</div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* NOTES TAB */}
          {activeTab === 'notes' && (
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Programming Notes</div>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px', lineHeight: '1.5' }}>
                  Add notes to help the AI workout generator understand how to use this exercise.
                </div>
              </div>
              <textarea
                value={programmingNotes}
                onChange={(e) => setProgrammingNotes(e.target.value)}
                placeholder="Example: Good finisher exercise. Pairs well with hip hinge movements. Don't program after heavy pressing."
                style={{ width: '100%', height: '200px', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', lineHeight: '1.6', resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
              />
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#999', textAlign: 'right' }}>{programmingNotes.length} characters</div>
            </div>
          )}

          {/* SUBSTITUTES TAB - FIXED WITH PROPER THUMBNAIL/SPRITE SUPPORT */}
          {activeTab === 'substitutes' && (
            <>
              <div style={{ padding: '12px 20px', background: '#fff8e1', borderBottom: '1px solid #ffe082', fontSize: '12px', color: '#f57c00', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>💡</span>
                <span>Select exercises that can replace this one if a user can't perform it or prefers an alternative</span>
              </div>

              <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', minHeight: '70px' }}>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Selected Substitutes</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {selectedSubstitutes.map(subId => {
                    const subAsset = allAssets.find(a => a.id === subId);
                    if (!subAsset) return null;
                    return (
                      <span key={subId} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#fce4ec', color: '#c2185b', padding: '6px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: 500 }}>
                        {subAsset.spriteUrl ? (
                          <div style={{ width: '24px', height: '24px', borderRadius: '4px', backgroundImage: `url(${subAsset.spriteUrl})`, backgroundSize: '400% 100%', backgroundPosition: '0% 0' }} />
                        ) : subAsset.thumbnailUrl ? (
                          <img src={subAsset.thumbnailUrl} alt="" style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '24px', height: '24px', borderRadius: '4px', background: '#ddd' }} />
                        )}
                        {subAsset.name || 'Untitled'}
                        <button onClick={() => toggleSubstitute(subId)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', opacity: 0.7 }}><X size={14} color="#c2185b" /></button>
                      </span>
                    );
                  })}
                  {selectedSubstitutes.length === 0 && <span style={{ color: '#bbb', fontSize: '13px', fontStyle: 'italic' }}>No substitutes selected</span>}
                </div>
              </div>

              <div style={{ padding: '12px 20px' }}>
                <div style={{ position: 'relative' }}>
                  <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                  <input type="text" placeholder="Search exercises..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px 10px 40px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ maxHeight: '280px', overflowY: 'auto', padding: '0 12px 12px' }}>
                {loading ? <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Loading exercises...</div> : (
                  <>
                    {filterAssets(allAssets).map(videoAsset => {
                      const isSelected = selectedSubstitutes.includes(videoAsset.id);
                      const movements = videoAsset.movementCategories || [];
                      const primaryMuscles = videoAsset.primaryBodyGroups || [];
                      return (
                        <div key={videoAsset.id} onClick={() => toggleSubstitute(videoAsset.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', background: isSelected ? '#fce4ec' : 'transparent', transition: 'background 0.15s' }}>
                          {videoAsset.spriteUrl ? (
                            <div style={{ width: '60px', height: '40px', borderRadius: '4px', backgroundImage: `url(${videoAsset.spriteUrl})`, backgroundSize: '400% 100%', backgroundPosition: '0% 0', flexShrink: 0 }} />
                          ) : videoAsset.thumbnailUrl ? (
                            <img src={videoAsset.thumbnailUrl} alt="" style={{ width: '60px', height: '40px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }} />
                          ) : (
                            <div style={{ width: '60px', height: '40px', borderRadius: '4px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '10px', flexShrink: 0 }}>▶</div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '14px', fontWeight: 500, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{videoAsset.name || 'Untitled'}</div>
                            <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                              {movements.slice(0, 1).map(m => {
                                const movement = MOVEMENT_CATEGORIES.find(mc => mc.id === m);
                                return movement ? (
                                  <span key={m} style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '3px', background: movement.color + '20', color: movement.color, fontWeight: 500 }}>{movement.name}</span>
                                ) : null;
                              })}
                              {primaryMuscles.slice(0, 1).map(muscle => (
                                <span key={muscle} style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '3px', background: '#e8f5e9', color: '#388e3c', fontWeight: 500 }}>{muscle}</span>
                              ))}
                            </div>
                          </div>
                          <div style={{ width: '22px', height: '22px', borderRadius: '50%', border: isSelected ? 'none' : '2px solid #ddd', background: isSelected ? '#e91e63' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {isSelected && <Check size={13} color="white" strokeWidth={3} />}
                          </div>
                        </div>
                      );
                    })}
                    {filterAssets(allAssets).length === 0 && (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>No exercises found</div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 20px', borderTop: '1px solid #eee', background: '#fafafa' }}>
            <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid #ddd', background: 'white', fontSize: '14px', fontWeight: 500, cursor: 'pointer', color: '#666' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', borderRadius: '6px', border: 'none', background: saving ? '#90caf9' : '#2196F3', fontSize: '14px', fontWeight: 500, cursor: saving ? 'default' : 'pointer', color: 'white' }}>{saving ? 'Saving...' : 'Save Tags'}</button>
          </div>
        </div>

        {/* Equipment Preview Panel */}
        {activeTab === 'equipment' && hoveredEquipment && (
          <div style={{ width: '200px', background: 'white', borderRadius: '12px', boxShadow: '0 8px 40px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            {hoveredEquipment.imageUrl ? (
              <img src={hoveredEquipment.imageUrl} alt={hoveredEquipment.name} style={{ width: '100%', height: '160px', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '160px', background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Image size={48} color="#bbb" />
              </div>
            )}
            <div style={{ padding: '16px' }}>
              <div style={{ fontSize: '16px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>{hoveredEquipment.name}</div>
              {hoveredEquipment.abbrev && <div style={{ fontSize: '13px', color: '#888', fontFamily: 'monospace' }}>{hoveredEquipment.abbrev}</div>}
            </div>
          </div>
        )}
      </div>

      {/* Add Body Group Modal */}
      {showAddBodyGroup && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }}>
          <div style={{ width: '340px', background: 'white', borderRadius: '12px', boxShadow: '0 8px 40px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #eee' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#333' }}>Add Body Group</h3>
              <button onClick={() => setShowAddBodyGroup(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}><X size={20} color="#666" /></button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#555', marginBottom: '6px' }}>Name</label>
                <input type="text" value={newBodyGroupName} onChange={(e) => setNewBodyGroupName(e.target.value)} placeholder="e.g., Rotator Cuff"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#555', marginBottom: '6px' }}>Category</label>
                <select value={newBodyGroupCategory} onChange={(e) => setNewBodyGroupCategory(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}>
                  <option value="Upper Body">Upper Body</option>
                  <option value="Core">Core</option>
                  <option value="Lower Body">Lower Body</option>
                </select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#555', marginBottom: '6px' }}>Color</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {['#e53935', '#d81b60', '#8e24aa', '#5e35b1', '#3949ab', '#1e88e5', '#00acc1', '#00897b', '#43a047', '#7cb342', '#fdd835', '#ffb300', '#fb8c00', '#f4511e', '#6d4c41', '#757575'].map(color => (
                    <div key={color} onClick={() => setNewBodyGroupColor(color)}
                      style={{ width: '28px', height: '28px', borderRadius: '50%', background: color, cursor: 'pointer', border: newBodyGroupColor === color ? '3px solid #333' : '3px solid transparent' }} />
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 20px', borderTop: '1px solid #eee', background: '#fafafa' }}>
              <button onClick={() => setShowAddBodyGroup(false)} style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid #ddd', background: 'white', fontSize: '14px', fontWeight: 500, cursor: 'pointer', color: '#666' }}>Cancel</button>
              <button onClick={handleAddBodyGroup} disabled={!newBodyGroupName.trim()} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', background: !newBodyGroupName.trim() ? '#a5d6a7' : '#4caf50', fontSize: '14px', fontWeight: 500, cursor: !newBodyGroupName.trim() ? 'default' : 'pointer', color: 'white' }}>Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditTagsModal;